import {useRef, useState, type FocusEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {
  mintIncapacidadUploadTarget,
  useCrearIncapacidadParaEmpleado,
  useEmployeeIncapacidades,
  useRechazarIncapacidad,
  useSubirCertificadoIncapacidad,
  useValidarIncapacidad,
  type Incapacidad,
  type IncapacidadEstado,
  type IncapacidadOrigen,
} from '@/hooks/empresa/mi_espacio/use-incapacidades'
import {
  EVIDENCE_ACCEPT,
  EVIDENCE_ALLOWED_TYPES,
  EVIDENCE_MAX_BYTES,
  evidenceTooBig,
  uploadToStorage,
} from '@/hooks/sgsst/evidencias/use-evidence'
import {CertificadoPicker} from '@/components/incapacidades/certificado-picker'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {translateApiError} from '@/lib/errors'
import {localToday, formatDateOnly} from '@/lib/risk'
import {validateRequired} from '@/lib/validation'
import {cn} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

function incapacidadEstadoTone(estado: IncapacidadEstado): 'green' | 'amber' | 'red' {
  if (estado === 'validada') return 'green'
  if (estado === 'rechazada') return 'red'
  return 'amber'
}

/** Sin certificado el backend responde 409, así que el botón se apaga con su motivo. */
function ValidarIncapacidadButton({businessId, incapacidad}: {businessId: string; incapacidad: Incapacidad}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const validar = useValidarIncapacidad(businessId)
  const sinCertificado = incapacidad.certificado === null

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        disabled={sinCertificado}
        title={sinCertificado ? t('incapacidades.validar.sinCertificado') : undefined}
        onClick={() => setOpen(true)}
        data-testid={`incapacidad-validar-${incapacidad.id}`}>
        {t('incapacidades.validar.action')}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={t('incapacidades.validar.title')}
        description={t('incapacidades.validar.description')}
        confirmLabel={t('incapacidades.validar.confirm')}
        cancelLabel={t('common.cancel')}
        pending={validar.isPending}
        onConfirm={() => {
          setError(null)
          validar.mutate(incapacidad.id, {
            onSuccess: () => setOpen(false),
            onError: (err) => setError(translateApiError(err)),
          })
        }}
      />
      {error && (
        <p className="mt-1 text-xs text-destructive" data-testid="incapacidad-validar-error">
          {error}
        </p>
      )}
    </>
  )
}

function RechazarIncapacidadButton({businessId, incapacidad}: {businessId: string; incapacidad: Incapacidad}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [motivoError, setMotivoError] = useState<string | undefined>()
  const [error, setError] = useState<string | null>(null)
  const rechazar = useRechazarIncapacidad(businessId)

  function blurMotivo(e: FocusEvent<HTMLTextAreaElement>) {
    setMotivoError(validateRequired(e.target.value) ?? undefined)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const err = validateRequired(motivo)
    setMotivoError(err ?? undefined)
    if (err) return
    setError(null)
    rechazar.mutate(
      {incapacidadId: incapacidad.id, motivo_rechazo: motivo.trim()},
      {
        onSuccess: () => {
          setOpen(false)
          setMotivo('')
        },
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`incapacidad-rechazar-${incapacidad.id}`}>
          {t('incapacidades.rechazar.action')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('incapacidades.rechazar.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <Textarea
            value={motivo}
            required
            minLength={1}
            maxLength={2000}
            data-testid="incapacidad-motivo-rechazo"
            placeholder={t('incapacidades.rechazar.motivoPlaceholder')}
            onChange={(e) => {
              setMotivo(e.target.value)
              setMotivoError(undefined)
            }}
            onBlur={blurMotivo}
            aria-invalid={!!motivoError}
          />
          <FieldError error={motivoError} />
          <FormDialogFooter
            error={error}
            pending={rechazar.isPending}
            submitLabel={t('incapacidades.rechazar.confirm')}
            onCancel={() => setOpen(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

function CertificadoUpload({businessId, incapacidad}: {businessId: string; incapacidad: Incapacidad}) {
  const {t} = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const subir = useSubirCertificadoIncapacidad(businessId)
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function start(files: FileList | null) {
    const file = files?.[0]
    if (!file) return
    setError(null)
    if (!EVIDENCE_ALLOWED_TYPES.has(file.type)) return setError(t('errors.evidenceBadFormat'))
    if (file.size > EVIDENCE_MAX_BYTES) return setError(evidenceTooBig(t, file))
    try {
      setProgress(0)
      const target = await mintIncapacidadUploadTarget(businessId, incapacidad.id, file)
      await uploadToStorage(target, file, setProgress)
      await subir.mutateAsync({incapacidadId: incapacidad.id, objectKey: target.object_key, file})
      setProgress(null)
    } catch (err) {
      setProgress(null)
      setError(translateApiError(err))
    }
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className="mt-1 space-y-1">
      <input
        ref={inputRef}
        type="file"
        accept={EVIDENCE_ACCEPT}
        hidden
        data-testid={`incapacidad-certificado-input-${incapacidad.id}`}
        onChange={(e) => start(e.target.files)}
      />
      {progress !== null ? (
        <div className="h-1.5 w-32 overflow-hidden rounded-full bg-secondary">
          <div className="h-full rounded-full bg-primary transition-all" style={{width: `${progress}%`}} />
        </div>
      ) : (
        <Button
          size="sm"
          variant="outline"
          disabled={subir.isPending}
          data-testid={`incapacidad-certificado-upload-${incapacidad.id}`}
          onClick={() => inputRef.current?.click()}>
          {t(incapacidad.certificado ? 'incapacidades.certificadoReponer' : 'incapacidades.certificadoSubir')}
        </Button>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

export function IncapacidadRow({
  businessId,
  incapacidad,
  canOperate,
  canUploadCertificado = false,
}: {
  businessId: string
  incapacidad: Incapacidad
  canOperate: boolean
  // El reportante carga su propio certificado (spec §5); `canOperate` decide validar o rechazar.
  canUploadCertificado?: boolean
}) {
  const {t} = useTranslation()
  const puedeDecidir = canOperate && incapacidad.estado === 'reportada'
  // El operador también carga el certificado: el backend lo admite en `_mine_or_operador_or_404`.
  const puedeSubirCertificado = (canUploadCertificado || canOperate) && incapacidad.estado === 'reportada'
  return (
    <li
      className="flex flex-wrap items-start justify-between gap-3 border-b border-border py-3 last:border-b-0"
      data-testid="incapacidad-row">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground">
          {formatDateOnly(incapacidad.fecha_inicio)} —{' '}
          {incapacidad.fecha_fin ? formatDateOnly(incapacidad.fecha_fin) : t('incapacidades.enCurso')}
          {incapacidad.dias_incapacidad !== null && (
            <span className="ml-1 font-mono text-xs text-muted-foreground">
              ({t('incapacidades.dias', {count: incapacidad.dias_incapacidad})})
            </span>
          )}
        </p>
        <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span data-testid="incapacidad-employee-nombre">{incapacidad.employee_nombre}</span>
          <Tag tone="neutral">{t(`incapacidades.origen.${incapacidad.origen}`)}</Tag>
          {incapacidad.entidad && <span>{incapacidad.entidad}</span>}
        </p>
        {incapacidad.estado === 'rechazada' && incapacidad.motivo_rechazo && (
          <p className="mt-1 text-xs text-destructive" data-testid="incapacidad-motivo">
            {incapacidad.motivo_rechazo}
          </p>
        )}
        {incapacidad.solapa_con_id && (
          <p className="mt-1 text-xs text-tone-amber-fg" data-testid="incapacidad-solape-advertencia">
            {t('incapacidades.solapeAdvertencia')}
          </p>
        )}
        {incapacidad.certificado ? (
          <a
            href={incapacidad.certificado.download_url}
            target="_blank"
            rel="noreferrer"
            data-testid="incapacidad-certificado-link"
            className="mt-1 inline-block text-xs font-semibold text-primary hover:underline">
            {t('incapacidades.certificadoDescargar')}
          </a>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground" data-testid="incapacidad-certificado-falta">
            {t('incapacidades.certificadoFalta')}
          </p>
        )}
        {puedeSubirCertificado && <CertificadoUpload businessId={businessId} incapacidad={incapacidad} />}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Tag tone={incapacidadEstadoTone(incapacidad.estado)}>{t(`incapacidades.estado.${incapacidad.estado}`)}</Tag>
        {puedeDecidir && (
          <>
            <ValidarIncapacidadButton businessId={businessId} incapacidad={incapacidad} />
            <RechazarIncapacidadButton businessId={businessId} incapacidad={incapacidad} />
          </>
        )}
      </div>
    </li>
  )
}

const ORIGENES: IncapacidadOrigen[] = ['laboral', 'comun']

function RegistrarIncapacidadDialog({businessId, employeeId}: {businessId: string; employeeId: string}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [origen, setOrigen] = useState<IncapacidadOrigen>('comun')
  // nosemgrep: browser-clock-business-date -- date input default value, legitimate data-entry (not a compliance decision)
  const [fechaInicio, setFechaInicio] = useState(localToday())
  const [fechaFin, setFechaFin] = useState('')
  const [entidad, setEntidad] = useState('')
  const [notas, setNotas] = useState('')
  const [certFile, setCertFile] = useState<File | null>(null)
  const [certProgress, setCertProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const crear = useCrearIncapacidadParaEmpleado(businessId)
  const subirCertificado = useSubirCertificadoIncapacidad(businessId)

  function reset() {
    setOrigen('comun')
    setFechaInicio(localToday())
    setFechaFin('')
    setEntidad('')
    setNotas('')
    setCertFile(null)
    setCertProgress(null)
    setError(null)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      const incapacidad = await crear.mutateAsync({
        employee_id: employeeId,
        origen,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin || null,
        entidad: entidad.trim() || null,
        notas: notas.trim() || null,
      })
      if (certFile) {
        setCertProgress(0)
        const target = await mintIncapacidadUploadTarget(businessId, incapacidad.id, certFile)
        await uploadToStorage(target, certFile, setCertProgress)
        await subirCertificado.mutateAsync({
          incapacidadId: incapacidad.id,
          objectKey: target.object_key,
          file: certFile,
        })
      }
      setOpen(false)
    } catch (err) {
      setCertProgress(null)
      setError(translateApiError(err))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (next) reset()
      }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="incapacidad-registrar">
          {t('incapacidades.registrar.action')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('incapacidades.registrar.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="flex flex-col gap-2">
            {ORIGENES.map((value) => (
              <button
                key={value}
                type="button"
                data-testid={`incapacidad-registrar-origen-${value}`}
                onClick={() => setOrigen(value)}
                className={cn(
                  'min-h-11 w-full rounded-lg border px-3 text-sm font-medium',
                  origen === value ? 'border-primary bg-accent text-accent-foreground' : 'border-border'
                )}>
                {t(`incapacidades.origen.${value}`)}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-3">
            <label className="space-y-1">
              <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('incapacidades.reportar.fechaInicioLabel')}
              </span>
              <input
                type="date"
                value={fechaInicio}
                data-testid="incapacidad-registrar-fecha-inicio"
                // nosemgrep: browser-clock-business-date -- date input max, legitimate data-entry (not a compliance decision)
                max={localToday()}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 font-mono text-sm shadow-sm focus:border-ring focus:outline-none"
              />
            </label>
            <label className="space-y-1">
              <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                {t('incapacidades.reportar.fechaFinLabel')}
              </span>
              <input
                type="date"
                value={fechaFin}
                min={fechaInicio}
                data-testid="incapacidad-registrar-fecha-fin"
                onChange={(e) => setFechaFin(e.target.value)}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 font-mono text-sm shadow-sm focus:border-ring focus:outline-none"
              />
            </label>
          </div>
          <Input
            value={entidad}
            maxLength={tope('RAZ')}
            data-testid="incapacidad-registrar-entidad"
            placeholder={t('incapacidades.reportar.entidadLabel')}
            onChange={(e) => setEntidad(e.target.value)}
          />
          <Textarea
            value={notas}
            maxLength={2000}
            data-testid="incapacidad-registrar-notas"
            placeholder={t('incapacidades.reportar.notasLabel')}
            onChange={(e) => setNotas(e.target.value)}
          />
          <div className="space-y-1">
            <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('incapacidades.reportar.certificadoLabel')}
            </span>
            <p className="text-xs text-muted-foreground">{t('incapacidades.registrar.certificadoHint')}</p>
            <CertificadoPicker
              file={certFile}
              progress={certProgress}
              onPick={setCertFile}
              onError={setError}
              testIdPrefix="incapacidad-registrar-certificado"
            />
          </div>
          <FormDialogFooter
            error={error}
            pending={crear.isPending || certProgress !== null}
            submitLabel={t('incapacidades.registrar.submit')}
            onCancel={() => setOpen(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function EmployeeIncapacidadesSection({
  businessId,
  employeeId,
  canOperate,
}: {
  businessId: string
  employeeId: string | null
  canOperate: boolean
}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  const [page, setPage] = useState(1)
  const {data, isLoading} = useEmployeeIncapacidades(businessId, employeeId, page)
  const incapacidades = data?.items

  if (isLoading && !data) return <Skeleton className="h-24 w-full" />
  return (
    <div className="space-y-3">
      {canOperate && !godReadOnly && employeeId && (
        <div className="flex justify-end">
          <RegistrarIncapacidadDialog businessId={businessId} employeeId={employeeId} />
        </div>
      )}
      {!incapacidades || incapacidades.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="employee-incapacidades-empty">
          {t('incapacidades.empty')}
        </p>
      ) : (
        <ul data-testid="employee-incapacidades-list">
          {incapacidades.map((incapacidad) => (
            <IncapacidadRow
              key={incapacidad.id}
              businessId={businessId}
              incapacidad={incapacidad}
              canOperate={canOperate}
              // Fuera de modo operador la lista es siempre la propia (el endpoint da 404 si no);
              // en modo dios de solo lectura, nunca.
              canUploadCertificado={!canOperate && !godReadOnly}
            />
          ))}
        </ul>
      )}
      {data && data.total > 0 && (
        <PaginationBar page={data.page} pageSize={data.page_size} total={data.total} onPageChange={setPage} />
      )}
    </div>
  )
}
